import {
  ActivityLevel,
  BodyCondition,
  Reproduction,
  TreatFrequency,
  Sex,
  Breeds,
} from "@/helpers/enums";
import { caloriesFromGrams } from "@/domains/nutrition/FoodEnergy";

/* -------------------------------------------------------
 * Helpers
 * -----------------------------------------------------*/
const clamp = (v: number, lo: number, hi: number) =>
  Math.max(lo, Math.min(hi, v));
const pow075 = (kg: number) => Math.pow(kg, 0.75);
const calcRER = (weightKg: number) => 70 * pow075(weightKg);

// ✅ round UP to nearest step (used for grams)
const roundUpToNearest = (value: number, step: number) =>
  Math.ceil(value / step) * step;

// allow numbers / strings / undefined – we always coerce with Number()
const monthsFromAge = (
  years: number | string | undefined,
  months: number | string | undefined
) => (Number(years) || 0) * 12 + (Number(months) || 0);

const isPuppy = (
  years: number | string | undefined,
  months: number | string | undefined
) => monthsFromAge(years, months) <= 12;

/* -------------------------------------------------------
 * INPUT TYPE: RELAXED VERSION OF DogForm
 * -----------------------------------------------------*/
export interface DogFormLike {
  // basic identity-ish stuff (not strictly used for maths, but fine to keep)
  name?: string;
  breed?: string | Breeds;
  sex?: string | Sex;

  // numeric fields we read for calculations
  years?: number | string;
  months?: number | string;
  ageYears?: number | string;
  ageMonths?: number | string;
  weightKg?: number | string;

  bodyCondition?: BodyCondition | string;
  reproduction?: Reproduction | string;

  allergies?: string[];
  healthIssues?: string[];

  activityLevel?: ActivityLevel | string;

  treatFrequency?: TreatFrequency | string;
  getsTreats?: boolean;

  dailyCalories?: number | string;
  dailyGrams?: number | string;

  // allow anything else the form might carry
  [key: string]: any;
}

/* -------------------------------------------------------
 * Display-label → enum reverse lookups
 * Account section stores display labels (e.g. "Male", "0–45 minutes/day")
 * while quiz uses enum values (e.g. "MALE", "LOW").
 * -----------------------------------------------------*/
const DISPLAY_TO_ACTIVITY: Record<string, ActivityLevel> = {
  "0–45 minutes/day": ActivityLevel.LOW,
  "45–90 minutes/day": ActivityLevel.MEDIUM,
  "90+ minutes/day": ActivityLevel.HIGH,
};

const DISPLAY_TO_BODY: Record<string, BodyCondition> = {
  Underweight: BodyCondition.UNDERWEIGHT,
  Ideal: BodyCondition.IDEAL,
  Overweight: BodyCondition.OVERWEIGHT,
};

const DISPLAY_TO_SEX: Record<string, Sex> = {
  Male: Sex.MALE,
  Female: Sex.FEMALE,
};

const DISPLAY_TO_REPRO: Record<string, Reproduction> = {
  Neutered: Reproduction.NEUTERED,
  Spayed: Reproduction.SPAYED,
  "Not neutered": Reproduction.NOT_NEUTERED,
  "Not spayed": Reproduction.NOT_SPAYED,
};

function normalizeActivity(v: any): ActivityLevel | undefined {
  if (Object.values(ActivityLevel).includes(v as ActivityLevel)) return v;
  return DISPLAY_TO_ACTIVITY[v] ?? undefined;
}

function normalizeBody(v: any): BodyCondition | undefined {
  if (Object.values(BodyCondition).includes(v as BodyCondition)) return v;
  return DISPLAY_TO_BODY[v] ?? undefined;
}

function normalizeSex(v: any): Sex | undefined {
  if (Object.values(Sex).includes(v as Sex)) return v;
  return DISPLAY_TO_SEX[v] ?? undefined;
}

function normalizeRepro(v: any): Reproduction | undefined {
  if (Object.values(Reproduction).includes(v as Reproduction)) return v;
  return DISPLAY_TO_REPRO[v] ?? undefined;
}

/* -------------------------------------------------------
 * Type guards for enums (fixes indexing errors)
 * -----------------------------------------------------*/
function isActivityLevel(value: any): value is ActivityLevel {
  return Object.values(ActivityLevel).includes(value as ActivityLevel);
}

function isBodyCondition(value: any): value is BodyCondition {
  return Object.values(BodyCondition).includes(value as BodyCondition);
}

function isSex(value: any): value is Sex {
  return Object.values(Sex).includes(value as Sex);
}

/* -------------------------------------------------------
 * Multipliers – aligned to config
 * -----------------------------------------------------*/

// Client spec:
// 0–2 months  = 2.0x
// 3–5 months  = 1.5x
// 6–12 months = 1.25x
// adult       = 1.0x
function ageMultiplier(
  years: number | string | undefined,
  months: number | string | undefined
) {
  const m = monthsFromAge(years, months);

  // 0–2 months (inclusive)
  if (m <= 2) return 2.0;

  // 3–5 months (inclusive)
  if (m <= 5) return 1.5;

  // 6–12 months (inclusive)
  if (m <= 12) return 1.25;

  // adult
  return 1.0;
}

// Config neuterMultiplier:
// NOT_NEUTERED/NOT_SPAYED: 1.25, NEUTERED/SPAYED: 1.2
function neuterMultiplier(repro?: Reproduction | string) {
  const norm = normalizeRepro(repro);
  if (!norm) return 1.2;

  const intact =
    norm === Reproduction.NOT_NEUTERED || norm === Reproduction.NOT_SPAYED;
  return intact ? 1.25 : 1.2;
}

/* -------------------------------------------------------
 * Activity multipliers
 * Config (compressed → LOW / MEDIUM / HIGH):
 *  - COUCH_POTATO: 0.9
 *  - SOMEWHAT_ACTIVE: 0.95
 *  - ACTIVE / VERY_ACTIVE / FULL_OF_ENERGY: 1.05
 *  - HEAVY_WORK ≈ 1.2 (we approximate via HIGH = 1.05)
 * -----------------------------------------------------*/
const activityMultiplierMap: Record<ActivityLevel, number> = {
  [ActivityLevel.LOW]: 0.9,
  [ActivityLevel.MEDIUM]: 0.95,
  [ActivityLevel.HIGH]: 1.05,
};

/* -------------------------------------------------------
 * Body condition multipliers
 * Config (5 → 3 buckets):
 *  - UNDERWEIGHT ≈ avg(1.2, 1.1) = 1.15
 *  - IDEAL = 1.0
 *  - OVERWEIGHT ≈ avg(0.9, 0.8) = 0.85
 * -----------------------------------------------------*/
const bcmMap: Record<BodyCondition, number> = {
  [BodyCondition.UNDERWEIGHT]: 1.15,
  [BodyCondition.IDEAL]: 1.0,
  [BodyCondition.OVERWEIGHT]: 0.85,
};

/* -------------------------------------------------------
 * Treat frequency – from config
 *  - puppyWithTreats: 1.2
 *  - adultWithTreats: 0.9
 *  - noTreats: 1
 * We treat HEAVY_SNACKER + OCCASIONAL as "with treats"
 * -----------------------------------------------------*/
function treatMultiplier(form: DogFormLike): number {
  const puppy = isPuppy(form.ageYears ?? form.years, form.ageMonths ?? form.months);

  const hasTreatsByFreq =
    form.treatFrequency === TreatFrequency.HEAVY_SNACKER ||
    form.treatFrequency === TreatFrequency.OCCASIONAL;

  const hasTreats = hasTreatsByFreq || form.getsTreats;

  if (!hasTreats) {
    // noTreats
    return 1.0;
  }

  // puppyWithTreats vs adultWithTreats
  return puppy ? 1.2 : 0.9;
}

/* -------------------------------------------------------
 * Gender multiplier (from config)
 *  - MALE: 1.03
 *  - FEMALE: 1
 * -----------------------------------------------------*/
const genderMultiplierMap: Record<Sex, number> = {
  [Sex.MALE]: 1.03,
  [Sex.FEMALE]: 1.0,
};

function genderMultiplier(sex?: Sex | string): number {
  const norm = normalizeSex(sex);
  if (!norm) return 1.0;
  return genderMultiplierMap[norm] ?? 1.0;
}

/* -------------------------------------------------------
 * MAIN CALCULATOR – NO SIZE/ SENIOR ADJUSTMENT
 * -----------------------------------------------------*/
export function calculateDailyIntakeFromForm(
  form: DogFormLike,
  opts: {
    kcalPerGram?: number;
    minCalories?: number;
    maxCalories?: number;
  } = {}
) {
  const {
    // from config
    kcalPerGram = 1.45,
    minCalories = 200,
    maxCalories = 5000,
  } = opts;
  const mealsPerDay = 2;

  const weightKg = clamp(Number(form.weightKg) || 1, 0.8, 100);

  // Support both quiz field names (years/months) and account field names (ageYears/ageMonths)
  const years = form.ageYears ?? form.years;
  const months = form.ageMonths ?? form.months;

  /* ---------------- RER ---------------- */
  const rer = calcRER(weightKg);

  /* ---------------- Base multiplier ---------------- */
  const puppy = isPuppy(years, months);
  const ageMult = ageMultiplier(years, months);
  const neuterMult = neuterMultiplier(form.reproduction);

  // For puppies we use age-based multiplier; for adults we use neuter status.
  const baseRerMult = puppy ? ageMult : neuterMult;

  /* ---------------- MER multipliers ---------------- */
  const normActivity = normalizeActivity(form.activityLevel);
  const actMult = normActivity
    ? activityMultiplierMap[normActivity]
    : 1.0;

  const normBody = normalizeBody(form.bodyCondition);
  const bcMult = normBody
    ? bcmMap[normBody]
    : 1.0;

  const genderMult = genderMultiplier(form.sex);

  // For puppies we don't further scale by activity / body condition;
  // for adults we do. Gender multiplier always applies.
  const merMultCore = puppy ? 1.0 : actMult * bcMult;
  const merMult = merMultCore * genderMult;

  /* ---------------- NO senior size adjustment ---------------- */
  const sizeAdjustment = 1.0;

  /* ---------------- MER ---------------- */
  const mer = rer * baseRerMult * merMult * sizeAdjustment;

  /* ---------------- Treats ---------------- */
  const treatsAdj = treatMultiplier(form);

  const requiredCalories = clamp(
    Math.round(mer * treatsAdj),
    minCalories,
    maxCalories
  );

  /* ---------------- Grams ---------------- */
  // ✅ Always round UP to the nearest 50g
  const rawGramsPerDay = requiredCalories / kcalPerGram;
  const gramsPerDay = roundUpToNearest(rawGramsPerDay, 50);
  const gramsPerMeal = Math.round((gramsPerDay / mealsPerDay) * 10) / 10;

  /* ---------------- Calories (from portioned grams) ---------------- */
  const dailyCalories = caloriesFromGrams(gramsPerDay);

  return {
    rer: Math.round(rer),
    mer: Math.round(mer),
    dailyCalories,
    gramsPerDay,
    gramsPerMeal,
    mealsPerDay,
    steps: {
      rerMultiplier: baseRerMult,
      merMultiplier: merMult, // includes gender multiplier
      treatMultiplier: treatsAdj,
      activityMultiplier: actMult,
      bodyConditionMultiplier: bcMult,
      neuterMultiplier: neuterMult,
      ageMultiplier: ageMult,
      sizeAdjustment, // always 1 now
    },
  };
}
